import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import TrackingMap from "../components/TrackingMap";
import LocationList from "../components/LocationList";

interface Order {
  id: string;
  customerName: string;
  customerEmail: string;
  amount: number;
  status: string;
  paymentMethod: string;
  createdAt: string;
}

interface Location {
  id: string;
  name: string;
  lat: number;
  lon: number;
  status: 'active' | 'pending' | 'delivered' | 'cancelled';
  type?: 'order' | 'driver' | 'warehouse' | 'customer';
  address?: string;
}

const Orders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    setOrders([
      { id: "ORD-001", customerName: "สมชาย ใจดี", customerEmail: "somchai@example.com", amount: 2500, status: "completed", paymentMethod: "PromptPay", createdAt: "2026-04-21" },
      { id: "ORD-002", customerName: "สมหญิง รักษ์ดี", customerEmail: "somying@example.com", amount: 1890, status: "pending", paymentMethod: "PromptPay", createdAt: "2026-04-21" },
      { id: "ORD-003", customerName: "วิชัย มั่งคั่ง", customerEmail: "wichai@example.com", amount: 3200, status: "shipped", paymentMethod: "PromptPay", createdAt: "2026-04-20" },
    ]);
  }, []);

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Map locations for delivery tracking
  const [mapLocations, setMapLocations] = useState<Location[]>([
    { id: 'ORD-001', name: 'สมชาย ใจดี', lat: 13.7563, lon: 100.5018, status: 'delivered', type: 'order', address: 'กรุงเทพมหานคร' },
    { id: 'ORD-002', name: 'สมหญิง รักษ์ดี', lat: 13.7246, lon: 100.5233, status: 'pending', type: 'order', address: 'กรุงเทพมหานคร' },
    { id: 'ORD-003', name: 'วิชัย มั่งคั่ง', lat: 13.7928, lon: 100.5683, status: 'active', type: 'driver', address: 'กรุงเทพมหานคร' },
  ]);

  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);

  const handleLocationClick = (location: Location) => {
    setSelectedLocation(location);
  };

  const handleStatusChange = (orderId: string, newStatus: string) => {
    setOrders(orders.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = { completed: "bg-green-100 text-green-800", pending: "bg-yellow-100 text-yellow-800", shipped: "bg-blue-100 text-blue-800", cancelled: "bg-red-100 text-red-800" };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = { completed: "สำเร็จ", pending: "รอดำเนินการ", shipped: "จัดส่งแล้ว", cancelled: "ยกเลิก" };
    return labels[status] || status;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">คำสั่งซื้อ</h1>
          <p className="text-gray-600 mb-6">จัดการและติดตามคำสั่งซื้อทั้งหมด</p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">คำสั่งซื้อทั้งหมด</p>
              <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">รอดำเนินการ</p>
              <p className="text-2xl font-bold text-yellow-600">{orders.filter((o) => o.status === "pending").length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">สำเร็จ</p>
              <p className="text-2xl font-bold text-green-600">{orders.filter((o) => o.status === "completed").length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
              <p className="text-sm text-gray-600">ยอดรวม</p>
              <p className="text-2xl font-bold text-blue-600">฿{orders.reduce((sum, o) => sum + o.amount, 0).toLocaleString()}</p>
            </div>
          </div>

          {/* Map Section for Delivery Tracking */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 bg-white rounded-lg shadow border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">แผนที่การจัดส่ง (Delivery Map)</h3>
              </div>
              <div className="h-96">
                <TrackingMap
                  locations={mapLocations}
                  center={[13.7563, 100.5018]}
                  zoom={12}
                  showRoute={true}
                  realtime={true}
                />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow border border-gray-200 h-96">
              <LocationList
                locations={mapLocations}
                onLocationClick={handleLocationClick}
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4 mb-6 border border-gray-200">
            <div className="flex gap-4">
              <input type="text" placeholder="ค้นหา..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg">
                <option value="all">สถานะทั้งหมด</option>
                <option value="pending">รอดำเนินการ</option>
                <option value="shipped">จัดส่งแล้ว</option>
                <option value="completed">สำเร็จ</option>
              </select>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">เลขคำสั่งซื้อ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ลูกค้า</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">จำนวนเงิน</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วิธีชำระ</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">วันที่</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">{order.id}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{order.customerName}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">฿{order.amount.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <select value={order.status} onChange={(e) => handleStatusChange(order.id, e.target.value)} className={`px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                        <option value="pending">รอดำเนินการ</option>
                        <option value="shipped">จัดส่งแล้ว</option>
                        <option value="completed">สำเร็จ</option>
                        <option value="cancelled">ยกเลิก</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{order.paymentMethod}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{order.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Orders;
